import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { Provider } from 'react-redux';
import { store } from './store/store';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './lib/react-query';
import { AuthProvider, useAuthContext } from './contexts/AuthContext';
import { ChatProvider } from './contexts/ChatContext';
import { SocketProvider } from './contexts/SocketContext';
import { ThemeProvider } from './components/ThemeProvider';
import { CrashLogButton } from './components/CrashLogButton';
import { Toaster } from './components/ui/sonner';
import { toast } from 'sonner';
import ErrorBoundary from './components/ErrorBoundary';
import { ExpoTokenHandler } from './components/ExpoTokenHandler';
import { ExpoTokenDebugPanelEnhanced } from './components/ExpoTokenDebugPanelEnhanced';
import { CDNDebugPanel } from './components/CDNDebugPanel';
import { ScrollRestoration } from './components/ScrollRestoration';
import { setupGlobalErrorHandlers, crashLogger } from './utils/crashLogger';
import { checkProductionReadiness } from './utils/productionConfig';
import { useAutoPrefetch } from './hooks/useDataCache';
import { register as registerServiceWorker } from './utils/serviceWorkerRegistration';
import { generateProfileSlug } from './utils/seo';
import './utils/socketDebugger';
import initDebugHelpers from './utils/debugHelpers';
import DebugScreenInfo from './components/DebugScreenInfo';
import { DebugLogger } from './components/DebugLogger';
import { logger } from './utils/logger';

import HomePage from './components/HomePage';
import LoginPage from './components/LoginPage';
import RegisterPage from './components/RegisterPage';
import NewRegistrationFlow from './components/NewRegistrationFlow';
import RegistrationFlow from './components/RegistrationFlow';
import VerificationScreen from './components/VerificationScreen';
import AdminPendingScreen from './components/AdminPendingScreen';
import ProfileCompletionForm from './components/ProfileCompletionForm';
import ProfileCompletionWrapper from './components/ProfileCompletionWrapper';
import PendingVerificationScreen from './components/PendingVerificationScreen';
import MobileApp from './components/MobileApp';
import AdminDashboardReal from './components/AdminDashboardReal';
import MarketingManagerDashboard from './components/marketing-manager/MarketingManagerDashboard';
import BlogsPage from './components/BlogsPage';
import CommunityPage from './components/CommunityPage';
import CommunityLoggedIn from './components/CommunityLoggedIn';
import ReferEarnPage from './components/ReferEarnPage';
import ReferEarnLoggedIn from './components/ReferEarnLoggedIn';
import EnhancedSearchPage from './components/EnhancedSearchPage';
import AdvancedMatchingPage from './components/AdvancedMatchingPage';
import HelpSupportPage from './components/HelpSupportPage';
import ContactUsPage from './components/ContactUsPage';
import ChangePasswordPage from './components/ChangePasswordPage';
import ResetPasswordPage from './components/ResetPasswordPage';
import EditProfilePageProduction from './components/EditProfilePageProduction';
import PremiumMarketplace from './components/PremiumMarketplace';
import TermsConditionsPage from './components/TermsConditionsPage';
import PremiumShop from './components/PremiumShop';
import { MarketingPanelPage } from './components/marketing/MarketingPanelPage';
import { KundliMaker } from './components/kundli/KundliMaker';
import { WordPressPluginDownload } from './components/WordPressPluginDownload';
import { WordPressPluginAdminPage } from './components/WordPressPluginAdminPage';
import APITestPage from './components/APITestPage';
import StaticPage from './components/StaticPage';
import ProfileView from './components/ProfileView';
import { SEOWrapper } from './components/SEOWrapper';
import { IncomingCallNotification } from './components/IncomingCallNotification';
import { WebRTCProvider } from './components/WebRTCProvider';
import { CallNotificationDebug } from './components/debug/CallNotificationDebug';
import ExpoTokenDebugPage from './pages/ExpoTokenDebugPage';
import { SuperInterestPopup } from './components/SuperInterestPopup';
import { RewardsPopup } from './components/RewardsPopup';
import { SmsInviteModal } from './components/SmsInviteModal';
import { AppDownloadHeaderBanner } from './components/AppDownloadHeaderBanner';
import { AppDownloadModal } from './components/AppDownloadModal';

setupGlobalErrorHandlers();

try {
  checkProductionReadiness();
} catch (error) {
  // Silent in production
}

export const isBlogSubdomain = () => {
  const hostname = window.location.hostname;
  return hostname.startsWith('blogs.') || hostname.includes('blogs.localhost');
};


function ProtectedRoute({ children, requiredRole }: { children: React.ReactNode; requiredRole?: 'admin' | 'marketing_manager' | 'marketing_user' }) {
  const { user, isAuthenticated, loading } = useAuthContext();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (loading || !isAuthenticated || !user) return;
    
    if (location.pathname === '/complete-profile' || location.pathname === '/admin-pending') {
      return;
    }
    
    const verificationStatus = user.adminVerification?.status;
    
    if (verificationStatus === 'pending') {
      navigate('/admin-pending', { replace: true });
      return;
    }
    
    if (verificationStatus === 'rejected' || !user.profileCompleted) {
      navigate('/complete-profile', { replace: true });
      return;
    }
    
    if (verificationStatus !== 'approved') {
      navigate('/admin-pending', { replace: true });
      return;
    }
  }, [user, isAuthenticated, loading, location.pathname, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto space-y-6">
          <div className="h-16 bg-white/50 rounded-lg animate-pulse" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-32 bg-white rounded-lg animate-pulse" />
            ))}
          </div>
          <div className="h-96 bg-white rounded-lg animate-pulse" />
        </div>
      </div>
    );
  }

  if (!isAuthenticated || !user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check if user has the required role
  if (requiredRole && user.role !== requiredRole) {
    // Development logging for debugging
    if (process.env.NODE_ENV === 'development') {
      console.log('🚫 Access Denied:');
      console.log('   - Required role:', requiredRole);
      console.log('   - User role:', user.role);
      console.log('   - Path:', location.pathname);
    }
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

function AuthRedirect() {
  const { user, isAuthenticated, loading } = useAuthContext();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (loading) return;

    if (isAuthenticated && user && (location.pathname === '/' || location.pathname === '/login' || location.pathname === '/register')) {
      if (isBlogSubdomain()) {
        navigate('/', { replace: true });
        return;
      }
      const verificationStatus = user.adminVerification?.status;
      
      if (verificationStatus === 'rejected' || !user.profileCompleted) {
        navigate('/complete-profile', { replace: true });
        return;
      }
      
      if (verificationStatus === 'pending') {
        navigate('/admin-pending', { replace: true });
        return;
      }
      
      if (verificationStatus === 'approved') {
        navigate('/app', { replace: true });
        return;
      }
      
      if (user.profileCompleted) {
        navigate('/admin-pending', { replace: true });
        return;
      }
      
      navigate('/complete-profile', { replace: true });
    }
  }, [isAuthenticated, user, loading, navigate, location]);

  return null;
}

function ProfileRouteHandler() {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Attempt automatic deep link to App/Play Store on Android
    const isAndroid = /android/i.test(navigator.userAgent);
    if (isAndroid) {
      const hasAttemptedRedirect = sessionStorage.getItem('appRedirectAttempted');
      if (!hasAttemptedRedirect) {
        sessionStorage.setItem('appRedirectAttempted', 'true');
        const slug = location.pathname.split('/').pop();
        // Chrome Android Intent format to force opening the app or falling back to the Play Store
        const intentUrl = `intent://atootbandan.com/profile/${slug}#Intent;scheme=https;package=com.atootbandanmatrimonialapp;S.browser_fallback_url=https://play.google.com/store/apps/details?id=com.atootbandanmatrimonialapp;end`;
        window.location.href = intentUrl;
      }
    }
  }, [location.pathname]);

  return (
    <ProfileView onClose={() => navigate(location.pathname.startsWith('/app') ? '/app' : '/')} />
  );
}

function AppContent() {
  const { user, isAuthenticated, loading, logout, loadUser } = useAuthContext();
  const navigate = useNavigate();

  useAutoPrefetch();

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
      toast.success('Logged out successfully');
    } catch (error) {
      toast.error('Failed to logout');
    }
  };

  const handleNavigate = (screen: string) => {
    logger.log('🔍 Navigation requested:', screen);
    const routeMap: Record<string, string> = {
      'home': '/',
      'login': '/login',
      'register': '/register',
      'registration-flow': '/complete-profile',
      'verification': '/verification',
      'app': '/app',
      'admin': '/admin',
      'marketing-manager': '/marketing-manager',
      'blogs': '/blogs',
      'community': '/community',
      'refer-earn': '/refer-earn',
      'search': '/search',
      'help': '/help',
      'contact-us': '/contact-us',
      'change-password': '/change-password',
      'edit-profile': '/edit-profile',
      'premium-marketplace': '/premium-marketplace',
      'terms': '/terms',
      'advanced-matching': '/advanced-matching',
      'premium-shop': '/premium-shop',
      'marketing-panel': '/marketing-panel',
      'kundli-maker': '/kundli-maker',
      'admin-pending': '/admin-pending',
      'api-test': '/api-test'
    };

    const route = routeMap[screen] || '/';
    logger.log('🚀 Navigating to route:', route);
    navigate(route);
  };

  const handleViewProfile = (profile: any) => {
    // Navigate to profile view page with proper slug generation
    const profileId = profile?._id || profile?.id;
    const firstName = profile?.userId?.firstName || profile?.firstName || 'User';
    const lastName = profile?.userId?.lastName || profile?.lastName || '';
    
    if (profileId) {
      const slug = generateProfileSlug(firstName, lastName, profileId);
      logger.log('📍 [App] Navigating to profile:', `/profile/${slug}`);
      navigate(`/profile/${slug}`);
    } else {
      logger.error('❌ [App] Unable to extract profile ID from:', profile);
      toast.error('Unable to view profile. Please try again.');
    }
  };

  if (loading && !user) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto space-y-6">
          <div className="h-16 bg-white/50 rounded-lg animate-pulse" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-32 bg-white rounded-lg animate-pulse" />
            ))}
          </div>
          <div className="h-96 bg-white rounded-lg animate-pulse" />
        </div>
      </div>
    );
  }

  return (
    <ThemeProvider>
      <ChatProvider>
        <SocketProvider>
          <WebRTCProvider>
            <div className="min-h-screen bg-gray-50 overflow-x-hidden">
              <AppDownloadHeaderBanner />
              <AuthRedirect />
              <ScrollRestoration />
              <Routes>
                <Route path="/" element={
                  isBlogSubdomain() ? (
                    <BlogsPage onBack={() => window.location.href = 'https://atootbandan.com'} isSubdomain={true} />
                  ) : (
                    <HomePage 
                      onNavigate={handleNavigate}
                      isLoggedIn={isAuthenticated}
                      userRole={user?.role as any}
                    />
                  )
                } />
                <Route path="/login" element={<LoginPage onNavigate={handleNavigate} />} />
                <Route path="/register" element={
                  <NewRegistrationFlow 
                    onNavigate={handleNavigate}
                    onComplete={() => navigate('/complete-profile')}
                  />
                } />
                <Route path="/complete-profile" element={
                  <ProfileCompletionWrapper 
                    onComplete={async () => {
                      try {
                        await loadUser();
                        navigate('/admin-pending');
                      } catch (error) {
                        navigate('/admin-pending');
                      }
                    }}
                  />
                } />
                <Route path="/verification" element={
                  <ProtectedRoute>
                    <VerificationScreen onComplete={() => navigate('/app')} />
                  </ProtectedRoute>
                } />
                <Route path="/profile/:slug" element={<ProfileRouteHandler />} />
                <Route path="/app" element={
                  <ProtectedRoute>
                    <MobileApp onLogout={handleLogout} onNavigate={handleNavigate} />
                  </ProtectedRoute>
                } />
                <Route path="/app/*" element={
                  <ProtectedRoute>
                    <MobileApp onLogout={handleLogout} onNavigate={handleNavigate} />
                  </ProtectedRoute>
                } />
                <Route path="/admin" element={
                  <ProtectedRoute requiredRole="admin">
                    <AdminDashboardReal onBack={() => navigate('/app/matches', { replace: true })} />
                  </ProtectedRoute>
                } />
                <Route path="/admin/:section" element={
                  <ProtectedRoute requiredRole="admin">
                    <AdminDashboardReal onBack={() => navigate('/app/matches', { replace: true })} />
                  </ProtectedRoute>
                } />
                <Route path="/marketing-manager" element={
                  <ProtectedRoute requiredRole="marketing_manager">
                    <MarketingManagerDashboard />
                  </ProtectedRoute>
                } />
                <Route path="/admin-pending" element={
                  <ProtectedRoute>
                    <AdminPendingScreen onLogout={handleLogout} />
                  </ProtectedRoute>
                } />
                <Route path="/blogs" element={
                  isBlogSubdomain() ? (
                    <Navigate to="/" replace />
                  ) : (
                    <BlogsPage onBack={() => navigate(isAuthenticated ? '/app' : '/')} />
                  )
                } />
                <Route path="/blogs/:slug" element={
                  isBlogSubdomain() ? (
                    <Navigate to="/" replace />
                  ) : (
                    <BlogsPage onBack={() => navigate(isAuthenticated ? '/app' : '/')} />
                  )
                } />
                <Route path="/community" element={
                  isAuthenticated ? (
                    <CommunityLoggedIn onBack={() => navigate('/app')} />
                  ) : (
                    <CommunityPage onBack={() => navigate('/')} />
                  )
                } />
                <Route path="/refer-earn" element={
                  isAuthenticated ? (
                    <ReferEarnLoggedIn onBack={() => navigate('/app')} />
                  ) : (
                    <ReferEarnPage onBack={() => navigate('/')} />
                  )
                } />
                <Route path="/search" element={
                  <EnhancedSearchPage onBack={() => navigate(isAuthenticated ? '/app' : '/')} />
                } />
                <Route path="/advanced-matching" element={
                  <AdvancedMatchingPage onBack={() => navigate(isAuthenticated ? '/app' : '/')} />
                } />
                <Route path="/help" element={
                  <HelpSupportPage 
                    onBack={() => navigate(isAuthenticated ? '/app' : '/')} 
                    isLoggedIn={isAuthenticated}
                  />
                } />
                <Route path="/contact-us" element={
                  <ContactUsPage 
                    onBack={() => navigate(isAuthenticated ? '/app' : '/')} 
                    isLoggedIn={isAuthenticated}
                  />
                } />
                <Route path="/change-password" element={
                  <ProtectedRoute>
                    <ChangePasswordPage onBack={() => navigate('/app')} />
                  </ProtectedRoute>
                } />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
                <Route path="/edit-profile" element={
                  <ProtectedRoute>
                    <EditProfilePageProduction onBack={() => navigate('/app')} />
                  </ProtectedRoute>
                } />
                <Route path="/premium-marketplace" element={
                  <PremiumMarketplace onBack={() => navigate(isAuthenticated ? '/app' : '/')} />
                } />
                <Route path="/terms" element={
                  <TermsConditionsPage onBack={() => navigate(isAuthenticated ? '/app' : '/')} />
                } />
                <Route path="/premium-shop" element={
                  <ProtectedRoute>
                    <PremiumShop onClose={() => navigate('/app')} />
                  </ProtectedRoute>
                } />
                <Route path="/marketing-panel" element={
                  <ProtectedRoute>
                    <MarketingPanelPage onClose={() => navigate('/app')} />
                  </ProtectedRoute>
                } />
                <Route path="/kundli-maker" element={
                  <KundliMaker onClose={() => navigate(isAuthenticated ? '/app' : '/')} />
                } />
                <Route path="/wordpress-plugin" element={
                  <ProtectedRoute>
                    <div className="min-h-screen bg-gray-50 p-6">
                      <div className="max-w-5xl mx-auto">
                        <WordPressPluginDownload />
                      </div>
                    </div>
                  </ProtectedRoute>
                } />
                <Route path="/wordpress-plugin-admin" element={
                  <ProtectedRoute requiredRole="admin">
                    <WordPressPluginAdminPage onBack={() => navigate('/admin')} />
                  </ProtectedRoute>
                } />
                <Route path="/api-test" element={
                  <ProtectedRoute>
                    <APITestPage onClose={() => navigate('/app')} />
                  </ProtectedRoute>
                } />
                <Route path="/expo-debug" element={
                  <ExpoTokenDebugPage />
                } />
                <Route path="/pages/:slug" element={
                  <StaticPage onBack={() => navigate(isAuthenticated ? '/app' : '/')} />
                } />
              </Routes>
            </div>
            <Toaster 
              position="top-center" 
              richColors 
              toastOptions={{
                classNames: {
                  toast: 'bg-white text-gray-950 border border-gray-200 shadow-lg',
                  description: 'text-gray-500',
                  actionButton: 'bg-gray-900 text-gray-50',
                  cancelButton: 'bg-gray-100 text-gray-500',
                  success: 'bg-green-50 text-green-900 border-green-200',
                  error: 'bg-red-50 text-red-900 border-red-200',
                  warning: 'bg-amber-50 text-amber-900 border-amber-200',
                  info: 'bg-blue-50 text-blue-900 border-blue-200',
                },
              }}
            />
            
            <IncomingCallNotification />
            {isAuthenticated && <SuperInterestPopup onViewProfile={handleViewProfile} />}
            {isAuthenticated && <RewardsPopup />}
            {isAuthenticated && <SmsInviteModal />}
            <AppDownloadModal />
            <DebugScreenInfo />
            <DebugLogger />
          </WebRTCProvider>
        </SocketProvider>
      </ChatProvider>
    </ThemeProvider>
  );
}

export default function App() {
  // Register service worker for image caching
  useEffect(() => {
    try {
      registerServiceWorker();
      initDebugHelpers();
    } catch (error) {
      // Silent fail - service worker is optional
    }
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <Provider store={store}>
          <BrowserRouter>
            <ErrorBoundary>
              <ExpoTokenHandler />
              <ExpoTokenDebugPanelEnhanced />
              <CDNDebugPanel />
              <AppContent />
            </ErrorBoundary>
          </BrowserRouter>
        </Provider>
      </AuthProvider>
    </QueryClientProvider>
  );
}